import { ZodObject, ZodType, z } from "zod/v4";
import { BOOL_VALUE } from "./ea.enums";

export const GENERATED_BY_DB_PROPERTIES = {
  createdAt: true,
  updatedAt: true,
  modifiedBy: true,
  id: true,
} as const;

export function omitNoStrict(schema, mask) {
  const newShape = { ...schema._zod.def.shape };
  const currDef = schema._zod.def; //.shape;
  for (const key in mask) {
    if (!(key in currDef.shape)) {
      continue;
    }
    if (!mask[key]) continue;
    delete newShape[key];
  }
  return z.clone(schema, {
    ...schema._zod.def,
    shape: newShape,
    checks: [],
  });
}

function makeOptionalPropsNullable<Schema extends z.ZodObject>(
  schema: Schema,
): z.ZodObject<
  {
    [key in keyof Schema["shape"]]: Schema["shape"][key] extends z.ZodOptional<infer T>
      ? z.ZodNullable<z.ZodOptional<T>>
      : Schema["shape"][key];
  },
  z.core.$strip
> {
  const entries = Object.entries(schema.shape) as [keyof Schema["shape"], z.ZodTypeAny][];
  const newProps = entries.reduce((acc, [key, value]) => {
    acc[key] =
      value instanceof z.ZodOptional ? (value.unwrap() as any).optional().nullable() : value;
    return acc;
  }, {} as any);

  return z.object(newProps) as any;
}

function convertNullsToUndefinedInOptionalProps<Schema extends z.ZodObject>(
  schema: Schema,
): z.ZodObject<
  {
    [key in keyof Schema["shape"]]: Schema["shape"][key] extends z.ZodOptional<infer T>
      ? z.ZodNullable<z.ZodOptional<T>>
      : Schema["shape"][key];
  },
  z.core.$strip
> {
  const entries = Object.entries(schema.shape) as [keyof Schema["shape"], z.ZodTypeAny][];
  const newProps = entries.reduce((acc, [key, value]) => {
    acc[key] =
      value instanceof z.ZodOptional
        ? value
            .unwrap()
            // @ts-ignore
            .optional()
            .nullable()
            .transform((v) => (v === null ? undefined : v))
        : value;
    return acc;
  }, {} as any);

  return z.object(newProps) as any;
}

function makeDefaultPropsOptional<Schema extends z.ZodObject>(
  schema: Schema,
): z.ZodObject<
  {
    [key in keyof Schema["shape"]]: Schema["shape"][key] extends z.ZodDefault<infer T>
      ? z.ZodOptional<z.ZodDefault<T>>
      : Schema["shape"][key];
  },
  z.core.$strip
> {
  const entries = Object.entries(schema.shape) as [keyof Schema["shape"], z.ZodTypeAny][];
  const newProps = entries.reduce((acc, [key, value]) => {
    acc[key] = value instanceof z.ZodDefault ? value.optional() : (value as any);
    return acc;
  }, {} as any);

  return z.object(newProps) as any;
}

type Flatten<T> = Identity<{
  [k in keyof T]: T[k];
}>;
type Identity<T> = T;

const generateEntitySchemas = <T extends z.ZodObject>(schema: T) => {
  const CreateSchema = omitNoStrict(
    ZodUtils.makeOptionalPropsNullable(schema),
    GENERATED_BY_DB_PROPERTIES,
  ) as ZodObject<
    Flatten<Omit<T["shape"], Extract<keyof T["shape"], keyof typeof GENERATED_BY_DB_PROPERTIES>>>
  >;

  return {
    general: schema,
    createData: ZodUtils.makeDefaultPropsOptional(CreateSchema),
    create: CreateSchema,
    update: CreateSchema.partial(),
  };
};

export const ZodUtils = {
  makeOptionalPropsNullable,
  convertNullsToUndefinedInOptionalProps,
  makeDefaultPropsOptional,
  generateEntitySchemas,
};

export type InferZodSchemaGroup<T> = {
  [Key in keyof T]: T[Key] extends ZodType<any, any> ? z.infer<T[Key]> : never;
};

export const BooleanSchema = z
  .union([z.boolean(), z.literal(BOOL_VALUE.TRUE), z.literal(BOOL_VALUE.FALSE)])
  .transform((value) => value === true || value === BOOL_VALUE.TRUE);

export const FunctionSchema = <T extends z.core.$ZodFunction>(schema: T) =>
  z.custom<Parameters<T["implement"]>[0]>((fn) => schema.implement(fn as any));
